import { CAR_STARTER_ID } from '../game/cars/CarCatalog';
import type { SponsorContract, SponsorId } from './SponsorSystem';
import { createContract } from './SponsorSystem';

export type ProgressionState = {
  unlocked: string[];
  money: number;
  xp: number;
  heatLevel: number;
  heatXP: number;
  sponsor: SponsorContract | null;
};

const STORAGE_PREFIX = 'car_race_progress_v2';
const getKey = (userId: string) => `${STORAGE_PREFIX}_${userId}`;

const defaultState = (): ProgressionState => ({
  unlocked: [CAR_STARTER_ID],
  money: 2500,
  xp: 0,
  heatLevel: 0,
  heatXP: 0,
  sponsor: null
});

export const getProgression = (userId: string): ProgressionState => {
  if (typeof window === 'undefined') return defaultState();
  try {
    const raw = window.localStorage.getItem(getKey(userId));
    if (!raw) return defaultState();
    const parsed = JSON.parse(raw) as Partial<ProgressionState> | null;
    const base = defaultState();
    const unlocked = Array.isArray(parsed?.unlocked) ? parsed!.unlocked : base.unlocked;
    return {
      ...base,
      ...parsed,
      unlocked: Array.from(new Set([CAR_STARTER_ID, ...unlocked]))
    } as ProgressionState;
  } catch {
    return defaultState();
  }
};

export const setProgression = (userId: string, state: ProgressionState) => {
  if (typeof window === 'undefined') return;
  const payload: ProgressionState = {
    ...state,
    unlocked: Array.from(new Set([CAR_STARTER_ID, ...(state.unlocked || [])]))
  };
  window.localStorage.setItem(getKey(userId), JSON.stringify(payload));
};

export const getUnlockedCars = (userId: string): string[] => getProgression(userId).unlocked;

export const setUnlockedCars = (userId: string, unlocked: string[]) => {
  const s = getProgression(userId);
  setProgression(userId, { ...s, unlocked });
};

export const unlockCar = (userId: string, carId: string): string[] => {
  const s = getProgression(userId);
  if (s.unlocked.includes(carId)) return s.unlocked;
  const next = [...s.unlocked, carId];
  setProgression(userId, { ...s, unlocked: next });
  return next;
};

export const getMoney = (userId: string) => getProgression(userId).money;
export const getXP = (userId: string) => getProgression(userId).xp;

export const addMoney = (userId: string, amount: number) => {
  const s = getProgression(userId);
  const next = Math.max(0, Math.floor(s.money + amount));
  setProgression(userId, { ...s, money: next });
  return next;
};

export const spendMoney = (userId: string, amount: number) => {
  const s = getProgression(userId);
  const next = Math.max(0, Math.floor(s.money - amount));
  setProgression(userId, { ...s, money: next });
  return next;
};

export const addXP = (userId: string, amount: number) => {
  const s = getProgression(userId);
  const next = Math.max(0, Math.floor(s.xp + amount));
  setProgression(userId, { ...s, xp: next });
  return next;
};

export const setHeatLevel = (userId: string, heatLevel: number) => {
  const s = getProgression(userId);
  const next = Math.max(0, Math.min(3, Math.floor(heatLevel)));
  setProgression(userId, { ...s, heatLevel: next });
  return next;
};

export const getHeatLevel = (userId: string) => getProgression(userId).heatLevel;

export const getSponsor = (userId: string) => getProgression(userId).sponsor;

export const startSponsor = (userId: string, id: SponsorId) => {
  const s = getProgression(userId);
  const contract = createContract(id);
  setProgression(userId, { ...s, sponsor: contract });
  return contract;
};

export const clearSponsor = (userId: string) => {
  const s = getProgression(userId);
  setProgression(userId, { ...s, sponsor: null });
};

export const setSponsor = (userId: string, sponsor: SponsorContract | null) => {
  const s = getProgression(userId);
  setProgression(userId, { ...s, sponsor });
};
